import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    DialogContentText,
    Button,
    TextField,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Grid,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import AppBarModal from '../../../../general/AppBarModal';
import BuscarPersona from '../../../../general/BuscarPersona';
import { mostrarError } from '../../../../../global/js/funciones';
import { BIENESTAR_MODAL_PAPER_FLEX } from '../../helpers/bienestarDetalleConstants';
import { fetchAsignarProfesional } from '../../services/bienestarDetalleService';

const PAPER_PROPS = { style: BIENESTAR_MODAL_PAPER_FLEX };
const CONTENT_STYLE = { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: 16 };

const obtenerNombreCompleto = (persona) => {
    if (!persona) return '';
    return [
        persona.primer_nombre,
        persona.segundo_nombre,
        persona.primer_apellido,
        persona.segundo_apellido,
    ]
        .filter(Boolean)
        .join(' ')
        .trim();
};

function DialogAsignarProfesional({
    open,
    solicitud,
    bienestarId,
    onClose,
    onAssigned,
    actualizarMensaje,
}) {
    const [profesionalId, setProfesionalId] = useState(null);
    const [profesionalNombre, setProfesionalNombre] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorSeleccion, setErrorSeleccion] = useState('');
    const [openBuscarPersona, setOpenBuscarPersona] = useState(false);
    const [confirmacionAbierta, setConfirmacionAbierta] = useState(false);

    const profesionalActual = useMemo(() => (
        solicitud?.profesional_asignado
        || solicitud?.detalle_agendamiento?.profesional_asignado
        || null
    ), [solicitud]);

    const nombreActual = useMemo(() => {
        if (!profesionalActual) return '';
        if (typeof profesionalActual === 'string') return profesionalActual;
        return obtenerNombreCompleto(profesionalActual);
    }, [profesionalActual]);

    const esReasignacion = Boolean(profesionalActual);
    const idResolved = bienestarId || solicitud?.id || null;

    const limpiarFormulario = useCallback(() => {
        setProfesionalId(null);
        setProfesionalNombre('');
        setErrorSeleccion('');
        setConfirmacionAbierta(false);
    }, []);

    useEffect(() => {
        if (!open) limpiarFormulario();
    }, [open, limpiarFormulario]);

    const cerrarModal = useCallback(() => {
        if (loading) return;
        if (typeof onClose === 'function') onClose();
    }, [loading, onClose]);

    const abrirBuscarPersona = useCallback(() => setOpenBuscarPersona(true), []);
    const cerrarBuscarPersona = useCallback(() => setOpenBuscarPersona(false), []);

    const seleccionarPersona = useCallback((data) => {
        if (!data) return;
        setProfesionalId(data.id);
        setProfesionalNombre(obtenerNombreCompleto(data));
        setErrorSeleccion('');
        cerrarBuscarPersona();
    }, [cerrarBuscarPersona]);

    const notificar = useCallback((titulo, tipo = 'info', tiempo = 4000) => {
        if (typeof actualizarMensaje === 'function') {
            actualizarMensaje({ titulo, tipo, mostrar: true, tiempo });
        }
    }, [actualizarMensaje]);

    const validarSeleccion = useCallback(() => {
        if (!profesionalId) {
            setErrorSeleccion('Debe seleccionar un profesional.');
            return false;
        }
        if (!idResolved) {
            notificar('No fue posible identificar la solicitud.', 'error');
            return false;
        }
        if (esReasignacion && profesionalActual?.id && Number(profesionalActual.id) === Number(profesionalId)) {
            setErrorSeleccion('El profesional seleccionado ya es el actual.');
            return false;
        }
        return true;
    }, [profesionalId, idResolved, esReasignacion, profesionalActual, notificar]);

    const solicitarConfirmacion = useCallback(() => {
        if (!validarSeleccion()) return;
        setConfirmacionAbierta(true);
    }, [validarSeleccion]);

    const cerrarConfirmacion = useCallback(() => {
        if (loading) return;
        setConfirmacionAbierta(false);
    }, [loading]);

    const ejecutarAsignacion = useCallback(async () => {
        setLoading(true);
        try {
            const { ok, estado, resp } = await fetchAsignarProfesional(idResolved, profesionalId);
            if (ok) {
                const tituloBase = esReasignacion
                    ? `Profesional reasignado correctamente a "${profesionalNombre}".`
                    : `Profesional "${profesionalNombre}" asignado correctamente.`;
                notificar(tituloBase, 'success');
                if (typeof onAssigned === 'function') onAssigned(resp);
                limpiarFormulario();
                if (typeof onClose === 'function') onClose();
                return;
            }
            const titulo = resp?.titulo || mostrarError(resp) || `No fue posible asignar el profesional${estado ? ` (cód. ${estado})` : ''}.`;
            notificar(titulo, 'error');
            setConfirmacionAbierta(false);
        } catch (e) {
            notificar(e?.message || 'Error al procesar la asignación del profesional.', 'error');
            setConfirmacionAbierta(false);
        } finally {
            setLoading(false);
        }
    }, [
        idResolved,
        profesionalId,
        profesionalNombre,
        esReasignacion,
        notificar,
        onAssigned,
        onClose,
        limpiarFormulario,
    ]);

    const tituloConfirmacion = esReasignacion ? 'Confirmar reasignación' : 'Confirmar asignación';
    const mensajeConfirmacion = esReasignacion
        ? `Se reemplazará al profesional actual${nombreActual ? ` "${nombreActual}"` : ''} por "${profesionalNombre}". El profesional anterior quedará registrado en el historial. ¿Desea continuar?`
        : `Se asignará al profesional "${profesionalNombre}" a esta solicitud. ¿Desea continuar?`;

    return (
        <>
            <Dialog
                open={open}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                onClose={cerrarModal}
                PaperProps={PAPER_PROPS}
            >
                <AppBarModal titulo='Asignar Profesional' mostrarModal={cerrarModal} titulo_accion='' />
                <DialogContent style={CONTENT_STYLE}>
                    <Grid container spacing={2}>
                        {esReasignacion && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">
                                    Profesional actual: <strong>{nombreActual || 'Sin nombre disponible'}</strong>
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label={esReasignacion ? 'Nuevo profesional' : 'Profesional asignado'}
                                placeholder="Buscar profesional"
                                value={profesionalNombre}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={abrirBuscarPersona} disabled={loading}>
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                error={Boolean(errorSeleccion)}
                                helperText={errorSeleccion}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ padding: '12px 16px' }}>
                    <Button onClick={cerrarModal} disabled={loading}>Cancelar</Button>
                    <div style={{ position: 'relative' }}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={solicitarConfirmacion}
                            disabled={loading || !profesionalId}
                        >
                            {esReasignacion ? 'Reasignar' : 'Asignar'}
                        </Button>
                        {loading && (
                            <CircularProgress
                                size={24}
                                style={{ position: 'absolute', top: '50%', left: '50%', marginTop: -12, marginLeft: -12 }}
                            />
                        )}
                    </div>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openBuscarPersona}
                onClose={cerrarBuscarPersona}
                fullWidth
                maxWidth="sm"
                aria-labelledby="bienestar-buscar-persona-title"
            >
                <AppBarModal
                    titulo="Buscar Persona"
                    mostrarModal={cerrarBuscarPersona}
                    titulo_accion=""
                    accion=""
                />
                <DialogContent style={{ padding: 1 }} className="scroll">
                    <BuscarPersona
                        open={openBuscarPersona}
                        cerrar={cerrarBuscarPersona}
                        callback={seleccionarPersona}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={confirmacionAbierta}
                onClose={cerrarConfirmacion}
                maxWidth="xs"
                fullWidth
                aria-labelledby="bienestar-confirmar-asignar-profesional"
            >
                <DialogTitle id="bienestar-confirmar-asignar-profesional">{tituloConfirmacion}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{mensajeConfirmacion}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cerrarConfirmacion} disabled={loading}>Cancelar</Button>
                    <div style={{ position: 'relative' }}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={ejecutarAsignacion}
                            disabled={loading}
                        >
                            Confirmar
                        </Button>
                        {loading && (
                            <CircularProgress
                                size={24}
                                style={{ position: 'absolute', top: '50%', left: '50%', marginTop: -12, marginLeft: -12 }}
                            />
                        )}
                    </div>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default React.memo(DialogAsignarProfesional);
